import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { useRecaptcha } from '../hooks/useRecaptcha';
import Spinner from './Spinner';

const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();
    const {
        isLoaded,
        isLoading: recaptchaLoading,
        error: recaptchaError,
        getToken,
    } = useRecaptcha();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            // Get reCAPTCHA token
            const recaptchaToken = await getToken('forgot_password');

            const response = await authApi.forgotPassword({ email, recaptchaToken });

            if (response.status === 'success') {
                setSuccess(
                    response.message ||
                        'If an account with this email exists, a password reset code will be sent.'
                );
                // Store email for the reset password flow
                sessionStorage.setItem('resetEmail', email);
                // Redirect to reset password page after a short delay
                setTimeout(() => {
                    navigate('/reset-password');
                }, 2000);
            } else {
                setError(response.message || 'Failed to send password reset email');
            }
        } catch (err) {
            if (err instanceof Error && err.message.includes('reCAPTCHA')) {
                setError('reCAPTCHA verification failed. Please try again.');
            } else {
                setError('An unexpected error occurred');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <div
            className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
            style={{ backgroundColor: '#1D1A31' }}
        >
            <div className='max-w-md w-full space-y-8'>
                {/* Header */}
                <div className='text-center'>
                    <div className='mx-auto flex items-center justify-center h-16 w-16 rounded-full border-2 border-white mb-4'>
                        <svg
                            className='h-8 w-8 text-white'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth='2'
                                d='M5 13l4 4L19 7'
                            ></path>
                        </svg>
                    </div>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>Reset Your Password</h1>
                    <p className='text-lg text-white'>
                        Enter your email address and we'll send you a code to reset your password.
                    </p>
                </div>

                {/* Reset Form */}
                <div className='bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100'>
                    <form onSubmit={handleSubmit} className='space-y-6'>
                        {/* Email Field */}
                        <div>
                            <label
                                htmlFor='email'
                                className='block text-sm font-medium text-gray-700 mb-2'
                            >
                                Email Address
                            </label>
                            <input
                                type='email'
                                id='email'
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                placeholder='Enter your email address'
                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed'
                            />
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                {error}
                            </div>
                        )}

                        {/* reCAPTCHA Error Message */}
                        {recaptchaError && (
                            <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>
                                reCAPTCHA Error: {recaptchaError}
                            </div>
                        )}

                        {/* Success Message */}
                        {success && (
                            <div className='bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm'>
                                {success}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type='submit'
                            disabled={loading || recaptchaLoading || !isLoaded}
                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black font-semibold py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                        >
                            {loading ? (
                                <div className='flex items-center justify-center'>
                                    <Spinner size='md' color='black' className='-ml-1 mr-3' />
                                    Sending...
                                </div>
                            ) : recaptchaLoading ? (
                                'Loading reCAPTCHA...'
                            ) : (
                                'Send Reset Code'
                            )}
                        </button>
                    </form>

                    {/* Back to Login Link */}
                    <div className='mt-6 text-center'>
                        <button
                            type='button'
                            onClick={handleBackToLogin}
                            disabled={loading}
                            className='text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
